import { useMemo } from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import RhfSelect, {
  type RhfSelectProps,
} from '@core/components/forms/fields/RhfSelect';
import type { Fabric } from '../../../api/fabric';
import FabricDialog from '../../components/FabricDialog';
import { useAppContext } from '../../context/AppContext';
import { useCreateActionOption } from '../../hooks/useCreateActionOption';
import { type FabricFlag, filterFabricsByFlag } from '../../utils/fabric';

type RhfFabricSelectProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label: string;
  flag?: FabricFlag;
} & Omit<
  RhfSelectProps<T, Fabric, Fabric>,
  'name' | 'label' | 'items' | 'getOptionValue' | 'getOptionLabel' | 'options'
>;

export function RhfFabricSelect<T extends FieldValues>({
  name,
  label,
  flag,
  ...rest
}: RhfFabricSelectProps<T>) {
  const { fabrics } = useAppContext();
  const items = useMemo(
    () => (flag ? filterFabricsByFlag(fabrics, flag) : fabrics),
    [fabrics, flag]
  );
  const fabricAction = useCreateActionOption<Fabric>(
    'Add new fabric…',
    (props) => <FabricDialog {...props} requiredFlag={flag} />
  );

  return (
    <RhfSelect<T, Fabric, Fabric>
      name={name}
      label={label}
      items={items}
      getOptionValue={(fabric) => String(fabric.id)}
      getOptionLabel={(fabric) => fabric.name}
      actionOption={fabricAction}
      {...rest}
    />
  );
}
